import {
  opArtifactCreatedAt,
  opArtifactDescription,
  opArtifactId,
  opArtifactIsPortfolio,
  opArtifactName,
  opArtifactType,
  opArtifactTypeName,
  opDict,
} from '@wandb/weave-ui/cg';
import {useNodeValue} from '@wandb/weave-ui/cgreact';
import * as Panel2 from '@wandb/weave-ui/components/Panel2/panel';
import moment from 'moment';
import * as React from 'react';
import {RefObject} from 'react';

import * as Generated from '../../../../generated/graphql';
import PanelArtifactTagList from '../../artifact/PanelArtifactTagList';
import {NotificationsOverviewWrapper} from './NotificationsOverviewWrapper';
import {OverviewItem} from './OverviewItem';
import * as S from './style';
import {TriggersOverview} from './TriggersOverview';

const artifactInputType = 'artifact' as const;
export const PanelArtifactMembershipOverviewCollectionSection: React.FC<
  Panel2.PanelProps<typeof artifactInputType, {}> & {
    readOnly: boolean;
    artifactRegistry?: boolean;
    inputRef: RefObject<HTMLDivElement> | null;
  }
> = props => {
  const {input: artifactNode, readOnly} = props;
  const [updateArtifactCollection] =
    Generated.useUpdateArtifactCollectionMutation();
  const [updateArtifactPortfolio] =
    Generated.useUpdateArtifactPortfolioMutation();
  const {result, loading} = useNodeValue(
    React.useMemo(() => {
      return opDict({
        artifactId: opArtifactId({
          artifact: artifactNode,
        }),
        artifactName: opArtifactName({
          artifact: artifactNode,
        }),
        artifactDescription: opArtifactDescription({
          artifact: artifactNode,
        }),
        artifactCreatedAt: opArtifactCreatedAt({
          artifact: artifactNode,
        }),
        artifactTypeName: opArtifactTypeName({
          artifactType: opArtifactType({artifact: artifactNode}),
        }),
        artifactIsPortfolio: opArtifactIsPortfolio({
          artifact: artifactNode,
        }),
      } as any);
    }, [artifactNode])
  );

  const onUpdateDescription: React.ComponentProps<
    typeof S.OverviewValueMarkdownEditor
  >['onChange'] = React.useCallback(
    (value: string) => {
      if (result?.artifactIsPortfolio) {
        updateArtifactPortfolio({
          variables: {
            artifactPortfolioID: result?.artifactId,
            description: value,
          },
        });
      } else {
        updateArtifactCollection({
          variables: {
            artifactSequenceID: result?.artifactId,
            description: value,
          },
        });
      }
    },
    [result, updateArtifactCollection, updateArtifactPortfolio]
  );

  return (
    <S.OverviewPanelSection>
      <S.OverviewPanelSectionHeader>
        <S.OverviewPanelSectionHeaderTitle>
          {result?.artifactIsPortfolio ? 'Model overview' : 'Artifact overview'}
        </S.OverviewPanelSectionHeaderTitle>
      </S.OverviewPanelSectionHeader>
      <S.OverviewPanelSectionBody>
        <S.OverviewPanelSectionBodyRow>
          <S.OverviewPanelSectionBodyColumn>
            {props.artifactRegistry && (
              <OverviewItem
                label={'Tags'}
                value={
                  <PanelArtifactTagList
                    {...Panel2.dummyProps}
                    input={artifactNode}
                    showButton={true}
                    wrap={true}
                    showColor={true}
                    tagSize={'large'}
                  />
                }
              />
            )}
            {!props.artifactRegistry && (
              <OverviewItem label={'Type'} value={result?.artifactTypeName} />
            )}
            <OverviewItem
              label={'Created At'}
              value={moment(
                moment.utc(result?.artifactCreatedAt).toDate()
              ).format('MMMM Do, YYYY')}
            />
            {props.artifactRegistry && (
              <>
                <OverviewItem
                  label="Automations"
                  value={
                    <TriggersOverview
                      {...Panel2.dummyProps}
                      input={artifactNode}
                      inputRef={props.inputRef}
                    />
                  }
                />
                <OverviewItem
                  label="Slack notifications"
                  value={
                    <NotificationsOverviewWrapper
                      {...Panel2.dummyProps}
                      input={artifactNode}
                    />
                  }
                />
              </>
            )}
          </S.OverviewPanelSectionBodyColumn>
        </S.OverviewPanelSectionBodyRow>
        <S.OverviewPanelSectionBodyRow>
          <OverviewItem
            label={'Description'}
            value={
              loading ? (
                <></>
              ) : (
                <S.OverviewValueMarkdownEditor
                  readOnly={readOnly}
                  saveText={false}
                  placeholder={'Add a markdown description...'}
                  serverText={result?.artifactDescription}
                  onChange={onUpdateDescription}
                />
              )
            }
          />
        </S.OverviewPanelSectionBodyRow>
      </S.OverviewPanelSectionBody>
    </S.OverviewPanelSection>
  );
};
